import React, { FC, ReactElement } from 'react'
import Button from '@mui/material/Button'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Grow from '@mui/material/Grow'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import Stack from '@mui/material/Stack'
import { ThemeProvider, createTheme } from '@mui/material'
import classNames from 'classnames'
import styles from './MenuList.module.css'

const theme = createTheme({
	typography: {
		button: {
			textTransform: 'none'
		}
	},
	components: {
		MuiButton: {
			styleOverrides: {
				root: {
					color: 'hsl(var(--muted-foreground))',
					padding: '0px',
					textAlign: 'left',
					width: '100%',
					justifyContent: 'start',
					minWidth: '0px'
				}
			}
		},
		MuiPaper: {
			defaultProps: {
				sx: {
					marginTop: '8px',
					borderRadius: '8px',
					backdropFilter: 'blur(12px)',
					background: 'hsl(var(--card) / 0.92)'
				}
			}
		},
		MuiList: {
			defaultProps: {
				sx: {
					ackdropFilter: 'blur(12px)'
				}
			},
			styleOverrides: {
				root: {
					height: '100%',
					background: 'transparent',
					padding: '8px 24px 8px 8px',
					borderRadius: '8px',
					display: 'flex',
					flexDirection: 'column',
					gap: '12px'
				}
			}
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					color: 'hsl(var(--foreground))',
					fontSize: 14,
					fontWeight: 500,
					padding: '8px',
					borderRadius: '8px'
				}
			}
		}
	}
})

const CustomMenuList: FC<{
	className?: string
	selected?: boolean
	name: string
	options: any[]
}> = (props): ReactElement => {
	const { options, name } = props
	const [open, setOpen] = React.useState(false)
	const anchorRef = React.useRef<HTMLButtonElement>(null)
	const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)
	const setAnchorRef = React.useCallback((node: HTMLButtonElement | null) => {
		anchorRef.current = node
		setAnchorEl(node)
	}, [])

	const handleToggle = () => {
		setOpen((prevOpen) => !prevOpen)
	}

	const handleClose = (event: Event | React.SyntheticEvent) => {
		if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
			return
		}

		setOpen(false)
	}

	function handleListKeyDown(event: React.KeyboardEvent) {
		if (event.key === 'Tab') {
			event.preventDefault()
			setOpen(false)
		} else if (event.key === 'Escape') {
			setOpen(false)
		}
	}

	// return focus to the button when we transitioned from !open -> open
	const prevOpen = React.useRef(open)
	React.useEffect(() => {
		if (prevOpen.current === true && open === false) {
			anchorRef.current!.focus()
		}

		prevOpen.current = open
	}, [open])

	const renderOption = (i: any) => {
		return (
			<MenuItem
				disableRipple={true}
				key={i.name}
				className={styles.menuItem}
				onClick={(e) => {
					handleClose(e)
					i.onClick()
				}}
			>
				{i.name}
			</MenuItem>
		)
	}

	return (
		<Stack direction="row" spacing={2}>
			<div style={{ width: '100%' }}>
				<ThemeProvider theme={theme}>
					<Button
							disableRipple={true}
							className={styles.button}
							ref={setAnchorRef}
						id="composition-button"
						aria-controls={open ? 'composition-menu' : undefined}
						aria-expanded={open ? 'true' : undefined}
						aria-haspopup="true"
						onClick={handleToggle}
					>
						<div
							className={classNames(
								{ [(props?.className as any)]: !!props?.className, [styles.selected]: props.selected },
								styles.name
							)}
						>
							{name}
						</div>
					</Button>
					<Popper
							open={open}
							anchorEl={anchorEl}
						role={undefined}
						placement="bottom-start"
						transition
						disablePortal
					>
						{({ TransitionProps, placement }) => (
							<Grow
								{...TransitionProps}
								style={{
									transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom'
								}}
							>
								<Paper>
									<ClickAwayListener onClickAway={handleClose}>
										<MenuList
											autoFocusItem={open}
											id="composition-menu"
											aria-labelledby="composition-button"
											onKeyDown={handleListKeyDown}
										>
											{options?.map(renderOption)}
										</MenuList>
									</ClickAwayListener>
								</Paper>
							</Grow>
						)}
					</Popper>
				</ThemeProvider>
			</div>
		</Stack>
	)
}

export default CustomMenuList
